import { useMutation } from '@apollo/client';
import { Add as AddIcon, Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Paper, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { DomainValidationMode } from 'Apollo/fragments/organization/DomainValidationMode';
import { CreateOrganizationDomain } from 'Apollo/mutations/organization/CreateOrganizationDomain';
import ContextualInfo from 'Components/ContextualInfo/ContextualInfo';
import { useColumnsDomains } from 'Hooks/useColumnsDomains';
import { useColumnsDomainsLight } from 'Hooks/useColumnsDomainsLight';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import TableCollection, { TableCollectionColumn } from 'Theme/components/TableCollection/TableCollection';
import { MAX_SELECTED } from 'Theme/config';
import mediaQueries from 'Theme/constants/mediaQueries';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTableStyles } from '../../Theme/styles/Table.styles';
import ActionOnSelectedDomains from './ActionOnSelectedDomains';

interface Props {
  loading: boolean;
  domains: IOrganizationDomain[];
  totalCount: number;
  organization: IOrganization;
  currentPage: number;
  onChangePage: (newPage: number) => void;
  rowsPerPage: number;
  onChangeRowsPerPage: (rpp: number) => void;
  onSelect: (ids: string[] | null) => void;
  onUnselect: (ids: string[] | null) => void;
  selectedIds: string[] | null;
}

export enum OrganizationDomainOrder {
  NameAsc = 'LASTNAMEASCTHENFIRSTNAMEASC',
  NameDesc = 'LASTNAMEDESCTHENFIRSTNAMEDESC',
  StatusAsc = 'STATUSASCTHENLASTNAMEASCTHENFIRSTNAMEASC',
  StatusDesc = 'STATUSDESCTHENLASTNAMEASCTHENFIRSTNAMEASC'
}

type DraftDomainRow = IOrganizationDomain & {
  draftId: string;
  isDraft: true;
};

type DomainTableRow = IOrganizationDomain | DraftDomainRow;

const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
const richTextValues = { b: (children: React.ReactNode) => <strong>{children}</strong> };

function OrganizationDomains({
  loading,
  domains,
  totalCount,
  organization,
  currentPage,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  onSelect,
  onUnselect,
  selectedIds
}: Readonly<Props>) {

  const isMobile = useMediaQuery(mediaQueries.mobile);
  const columns = useColumnsDomains();
  const columnsLight = useColumnsDomainsLight();
  const ts = useTranslate();
  const { onError, onHandle, onSuccess } = useNotification();
  const [draftRows, setDraftRows] = useState<DraftDomainRow[]>([]);
  const [createdRows, setCreatedRows] = useState<IOrganizationDomain[]>([]);
  const [loadingDraftIds, setLoadingDraftIds] = useState<Set<string>>(new Set());
  const [createOrganizationDomain] = useMutation(CreateOrganizationDomain);
  const classes = useTableStyles();

  const onAddDomainRow = () => {
    const draftId = `draft-domain-${Math.random().toString(16).slice(2)}`; // NOSONAR - Only used for temporary UI row identification, not security-sensitive
    setDraftRows(prevRows => [
      {
        draftId,
        isDraft: true,
        name: '',
        validated: false,
        validationMode: DomainValidationMode.None,
        creationDate: new Date().toISOString(),
        validationCodeExpirationDate: null,
        validationDate: null,
        validationCode: null,
        forceEmail: false,
        forceTotp: false,
        isPublished: false
      },
      ...prevRows
    ]);
  };

  const updateDraftName = (draftId: string, name: string) => {
    setDraftRows(prevRows => prevRows.map(row => (
      row.draftId === draftId ? { ...row, name } : row
    )));
  };

  const removeDraftRow = useCallback((draftId: string) => {
    setDraftRows(prevRows => prevRows.filter(row => row.draftId !== draftId));
  }, []);

  const adjustedTotalCount = totalCount + draftRows.length + createdRows.length;

  const tableRows = useMemo<DomainTableRow[]>(() => {
    const allDomains = [...domains, ...createdRows]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [...draftRows, ...allDomains];
  }, [draftRows, domains, createdRows]);

  const selectedColumns = isMobile ? columnsLight : columns;

  const handleAddDomain = useCallback((row: DraftDomainRow) => {
    setLoadingDraftIds(prev => new Set(prev).add(row.draftId));
    createOrganizationDomain({
      variables: {
        organizationCode: organization.code,
        domainName: row.name
      }
    }).then(({ data }) => {
      const result = data?.createOrganizationDomain;
      if (result?.error?.message) {
        onHandle([result.error.message]);
        return;
      }
      if (result) {
        setCreatedRows(prev => [...prev, result]);
        onSuccess('page.organizationDomains.successAdd');
      }
      removeDraftRow(row.draftId);
    }).catch(onError)
      .finally(() => setLoadingDraftIds(prev => {
        const next = new Set(prev);
        next.delete(row.draftId);
        return next;
      }));
  }, [createOrganizationDomain, organization.code, onHandle, onError, onSuccess, removeDraftRow]);

  const renderDraftNameField = useCallback((row: DraftDomainRow) => {
    const isInvalid = row.name.length > 0 && !domainRegex.test(row.name);
    return (
      <TextField
        value={row.name}
        size="small"
        variant="outlined"
        error={isInvalid}
        helperText={isInvalid ? ts('component.modals.createOrganization.errors.invalidDomain') : undefined}
        onChange={event => updateDraftName(row.draftId, event.target.value)}
      />
    );
  }, [ts]);

  const onReloadDomains = useCallback(() => {
    setCreatedRows([]);
    onSelect([]);
    onUnselect(null);
  }, [onSelect, onUnselect]);

  const renderActionsCell = useCallback((params: { row: DomainTableRow }) => {
    const { row } = params;
    if (!('isDraft' in row) || !row.isDraft) {
      return null;
    }

    const isValidDomain = domainRegex.test(row.name);

    return (
      <Stack direction="row" spacing={1}>
        {loadingDraftIds.has(row.draftId) ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <IconButton
              size="small"
              disabled={!isValidDomain}
              onClick={() => handleAddDomain(row)}
            >
              <CheckCircleIcon color={isValidDomain ? 'info' : 'disabled'} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => removeDraftRow(row.draftId)}
            >
              <CancelIcon sx={{ color: 'black' }} />
            </IconButton>
          </>
        )}
      </Stack>
    );
  }, [loadingDraftIds, handleAddDomain, removeDraftRow]);

  const tableColumns = useMemo<TableCollectionColumn[]>(() => {
    const updatedColumns = selectedColumns.map(column => {
      if (column.key !== 'name') {
        return column;
      }

      return {
        ...column,
        renderCell: ({ row }: { row: DomainTableRow }) => {
          if ('isDraft' in row && row.isDraft) {
            return renderDraftNameField(row);
          }

          return column.renderCell({ row });
        }
      };
    });

    return [
      ...updatedColumns,
      {
        key: 'actions',
        width: 0.12,
        renderHead: () => '',
        renderSkeleton: () => null,
        renderCell: renderActionsCell
      }
    ];
  }, [selectedColumns, renderActionsCell, renderDraftNameField]);

  return (
    <>
      <Helmet title={ts('page.organizations.title')} />
      <Paper className={classes.root}>
        <div className={classes.toolbar}>
          <div className={classes.toolbarLeft}>
            <Typography variant="h5" sx={{ display: { xs: 'none', md: 'block' } }}>
              {ts('page.organizationDomains.domains')}
            </Typography>
            <ContextualInfo
              content={ts('page.organizationDomains.domainsHelp', richTextValues)}
            />
          </div>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ p: 2 }}>
            <Button
              color="primary"
              startIcon={<AddIcon className={classes.addUserButtonIcon} />}
              onClick={onAddDomainRow}
            >
              {ts('page.organizationDomains.addDomain')}
            </Button>
          </Stack>
        </div>
        <TableCollection
          loading={loading}
          columns={tableColumns}
          rows={tableRows}
          getRowId={(d: DomainTableRow) => ('isDraft' in d && d.isDraft ? d.draftId : d.name)}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          page={currentPage}
          onChangePage={onChangePage}
          selectedIds={selectedIds}
          onUnselect={onUnselect}
          onSelect={onSelect}
          maxSelected={MAX_SELECTED}
          isSelectAllDisabled={false}
          totalCount={adjustedTotalCount}
          messages={{
            maxSelectedExceeded: 'page.organizationDomains.maxLimitExceeded',
            pageAllSelected: 'page.organizationDomains.pageSelectedDomainsCount',
            selectAll: 'page.organizationDomains.selectDomainsCount'
          }}
        />
      </Paper >

      <ActionOnSelectedDomains
        selectedIds={selectedIds ?? []}
        selectAllEnabled={selectedIds == null}
        totalCount={totalCount}
        organizationCode={organization.code}
        onReloadDomains={onReloadDomains}
        onSelect={onSelect}
      />
    </>
  );
}

export default OrganizationDomains;
